import { useEffect, useState } from "react";
import { Save, Bell, Mail, Smartphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import { useAuthStore } from "@/stores/authStore";
import { notificationService } from "@/lib/services/notifications";
import {
  enableWebPush,
  disableWebPush,
  isPushSupported,
  isPushConfigured,
} from "@/lib/services/push";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  DEFAULT_NOTIFICATION_PREFS,
  NOTIFICATION_CATEGORY_LABEL,
  type NotificationCategory,
  type NotificationPrefs,
} from "@/types";

const CATEGORIES: {
  id: NotificationCategory;
  desc: string;
}[] = [
  {
    id: "registration",
    desc: "Nuevas inscripciones, aprobaciones, rechazos y cancelaciones",
  },
  {
    id: "payment",
    desc: "Comprobantes subidos, pagos aprobados/rechazados, saldo cubierto",
  },
  {
    id: "club",
    desc: "Solicitudes de unión y cambios en tu club",
  },
  {
    id: "progress",
    desc: "Aprobaciones de clases y especialidades",
  },
  {
    id: "membership",
    desc: "Cumpleaños, renovaciones y membresías",
  },
  {
    id: "system",
    desc: "Verificaciones de cuenta y mensajes del sistema",
  },
];

export default function NotificationPreferences() {
  const uid = useAuthStore((s) => s.user?.uid);
  const [prefs, setPrefs] = useState<NotificationPrefs>(
    DEFAULT_NOTIFICATION_PREFS,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Push state
  const [pushSupported, setPushSupported] = useState<boolean>(true);
  const [pushConfigured, setPushConfigured] = useState<boolean>(true);
  const [pushPermission, setPushPermission] = useState<
    NotificationPermission | "default"
  >("default");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const supported = await isPushSupported();
      setPushSupported(supported);
      setPushConfigured(isPushConfigured());
      if (supported && typeof Notification !== "undefined") {
        setPushPermission(Notification.permission);
      }
    })();
  }, []);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        const userPrefs = snap.data()?.notificationPrefs as
          | NotificationPrefs
          | undefined;
        if (!cancelled) {
          setPrefs({
            ...DEFAULT_NOTIFICATION_PREFS,
            ...(userPrefs || {}),
            byCategory: {
              ...DEFAULT_NOTIFICATION_PREFS.byCategory,
              ...(userPrefs?.byCategory || {}),
            },
            byCategoryPush: {
              ...DEFAULT_NOTIFICATION_PREFS.byCategoryPush,
              ...(userPrefs?.byCategoryPush || {}),
            },
          });
        }
      } catch (err) {
        console.warn("loadPrefs failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const setEmail = (v: boolean) => setPrefs((p) => ({ ...p, emailEnabled: v }));

  const setCat = (cat: NotificationCategory, v: boolean) =>
    setPrefs((p) => ({
      ...p,
      byCategory: { ...(p.byCategory || {}), [cat]: v },
    }));

  const setCatPush = (cat: NotificationCategory, v: boolean) =>
    setPrefs((p) => ({
      ...p,
      byCategoryPush: { ...(p.byCategoryPush || {}), [cat]: v },
    }));

  const handleEnablePush = async () => {
    if (!uid) return;
    setPushError(null);
    setPushBusy(true);
    try {
      const res = await enableWebPush(uid);
      if (res.ok) {
        setPushPermission("granted");
        setPrefs((p) => ({ ...p, pushEnabled: true }));
        await notificationService.updatePrefs(uid, {
          ...prefs,
          pushEnabled: true,
        });
      } else {
        const map: Record<string, string> = {
          unsupported: "Tu navegador no soporta notificaciones push.",
          not_configured:
            "Push aún no está configurado en este entorno (falta VAPID key).",
          denied:
            "Permiso denegado. Habilítalo en la configuración del navegador.",
          error: "No se pudo activar push. Intenta de nuevo.",
        };
        setPushError(map[res.reason] || "Error desconocido");
        if (res.reason === "denied" && typeof Notification !== "undefined") {
          setPushPermission(Notification.permission);
        }
      }
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    if (!uid) return;
    setPushBusy(true);
    try {
      await disableWebPush(uid);
      setPrefs((p) => ({ ...p, pushEnabled: false }));
      await notificationService.updatePrefs(uid, {
        ...prefs,
        pushEnabled: false,
      });
    } finally {
      setPushBusy(false);
    }
  };

  const handleSave = async () => {
    if (!uid) return;
    setSaving(true);
    try {
      await notificationService.updatePrefs(uid, prefs);
      setSavedAt(new Date());
    } catch (err) {
      console.error("save prefs failed", err);
      alert("No se pudo guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="py-10 text-center text-gray-400 text-sm">
          Cargando preferencias…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center shadow-md shadow-rose-500/25">
            <Bell className="w-5 h-5 text-white" />
          </div>
          Preferencias de Notificaciones
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <p className="text-gray-500 text-sm">
            Las notificaciones siempre aparecen en tu campanita. Aquí controlas
            si además se envían por correo o como push notification.
          </p>

          {/* ─────────── PUSH NOTIFICATIONS ─────────── */}
          <div className="p-4 border-2 border-gray-100 rounded-xl space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <Smartphone className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    Notificaciones push
                  </p>
                  <p className="text-sm text-gray-500">
                    Recibe avisos en tiempo real, incluso con la pestaña
                    cerrada.
                  </p>
                </div>
              </div>
              {!pushSupported || !pushConfigured ? (
                <span className="text-xs text-gray-400 italic">
                  No disponible
                </span>
              ) : pushPermission === "granted" && prefs.pushEnabled ? (
                <Button
                  variant="outline"
                  onClick={handleDisablePush}
                  disabled={pushBusy}
                >
                  {pushBusy ? "..." : "Desactivar"}
                </Button>
              ) : (
                <Button onClick={handleEnablePush} disabled={pushBusy}>
                  {pushBusy ? "Activando..." : "Activar"}
                </Button>
              )}
            </div>
            {!pushSupported && (
              <p className="text-xs text-amber-600">
                Tu navegador no soporta web push (necesitas Chrome, Edge,
                Firefox o Safari macOS/iOS 16.4+).
              </p>
            )}
            {pushSupported && !pushConfigured && (
              <p className="text-xs text-amber-600">
                Push aún no está configurado en este entorno (falta VAPID key en
                variables de entorno).
              </p>
            )}
            {pushPermission === "denied" && (
              <p className="text-xs text-rose-600">
                Permiso bloqueado en el navegador. Habilítalo desde el ícono de
                candado/permisos junto a la URL.
              </p>
            )}
            {pushError && <p className="text-xs text-rose-600">{pushError}</p>}

            {/* Categorías push */}
            {pushSupported &&
              pushConfigured &&
              pushPermission === "granted" &&
              prefs.pushEnabled && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs uppercase font-semibold text-gray-400 tracking-wide mb-2 mt-2">
                    Tipos de push
                  </p>
                  <div className="space-y-1.5">
                    {CATEGORIES.map((c) => {
                      const v = prefs.byCategoryPush?.[c.id] !== false;
                      return (
                        <label
                          key={`push-${c.id}`}
                          className="flex items-center justify-between gap-3 py-1.5 px-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                        >
                          <span className="text-sm text-gray-700">
                            {NOTIFICATION_CATEGORY_LABEL[c.id]}
                          </span>
                          <input
                            type="checkbox"
                            checked={v}
                            onChange={(e) => setCatPush(c.id, e.target.checked)}
                            className="w-4 h-4 text-emerald-600 rounded shrink-0"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>

          {/* Master toggle email */}
          <label className="flex items-center justify-between gap-4 p-4 border-2 border-gray-100 rounded-xl hover:bg-gray-50 cursor-pointer transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  Recibir notificaciones por correo
                </p>
                <p className="text-sm text-gray-500">
                  Si lo desactivas, solo verás las notificaciones dentro de la
                  app.
                </p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={prefs.emailEnabled}
              onChange={(e) => setEmail(e.target.checked)}
              className="w-5 h-5 text-purple-600 rounded shrink-0"
            />
          </label>

          {/* Por categoría */}
          <div
            className={
              prefs.emailEnabled ? "" : "opacity-50 pointer-events-none"
            }
          >
            <p className="text-xs uppercase font-semibold text-gray-400 tracking-wide mb-3">
              Tipos de correo
            </p>
            <div className="space-y-2">
              {CATEGORIES.map((c) => {
                const v = prefs.byCategory?.[c.id] !== false;
                return (
                  <label
                    key={c.id}
                    className="flex items-center justify-between gap-4 p-3 border-2 border-gray-100 rounded-xl hover:bg-gray-50 cursor-pointer transition-all"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        {NOTIFICATION_CATEGORY_LABEL[c.id]}
                      </p>
                      <p className="text-sm text-gray-500">{c.desc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={v}
                      onChange={(e) => setCat(c.id, e.target.checked)}
                      className="w-4 h-4 text-purple-600 rounded shrink-0"
                    />
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-400">
              {savedAt
                ? `Guardado a las ${savedAt.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}`
                : ""}
            </p>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Guardando…" : "Guardar preferencias"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
